import React, { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { selectNewBeneState, selectTransactionReducer } from '../../redux/selectors';
import { Grid } from 'semantic-ui-react';
import { IStore } from '../../store.type';
import Search from '../search/search';
import { handleFetchPlaceDetails, handleFetchPlaceId } from './addressMethods';
import { NewAddressSearchContainer } from './styles';
import { ADDRESS_NOT_FOUND_MSG } from '../../utils/constants';
import { LOCATION_PIN_ICON } from '@Images';
import { EventButtonType, EventNamesV2 } from '@Utils/events/eventsConstants';
import { logEventV2 } from '@Utils/events/logUserEvents';

type NewAddressSearchProps = {
  iconPlacement: string;
  newSearchUi: boolean;
  countryCode?: string;
  retrievedValuesCallBack: (value: any) => void;
  searchPlaceHolder: string;
  beneAddressFlow?: boolean;
  isNewBeneAddressFlow?: boolean;
  onEnterManually?: () => void;
  boxInputSearchNewUi?: boolean;
  hideManualAddressLink?: boolean;
};

type SuggestionType = {
  key: string;
  value: string;
  title: string;
};
type StateType = {
  suggestions: Array<SuggestionType>;
  keepOpen: boolean;
  loading: boolean;
  retrievedAddress: any;
  showMessage: boolean;
};

const NewAddressSearch: React.FC<NewAddressSearchProps> = ({
  iconPlacement,
  newSearchUi,
  countryCode,
  retrievedValuesCallBack,
  searchPlaceHolder,
  beneAddressFlow,
  isNewBeneAddressFlow,
  onEnterManually,
  boxInputSearchNewUi,
  hideManualAddressLink
}: NewAddressSearchProps) => {
  const [state, setState] = useState<StateType>({
    suggestions: [],
    keepOpen: false,
    loading: false,
    retrievedAddress: {},
    showMessage: false
  });
  const getTransactionReducerData = useSelector(selectTransactionReducer);
  const {
    formValues: { beneficiary_country_code, country_code, bank_account_country },
    currentBene,
    isEditFlow
  } = useSelector(selectNewBeneState);

  const selectedBene = getTransactionReducerData?.transactionDetails?.selectedBene;
  const beneCountryCode = selectedBene?.country_code;
  // whenever either the sender or the beneficiary's bank account is AU-based.
  const isAustracCase = [countryCode, selectedBene?.bank_account_country].includes('AU');
  // AUSTRAC: same rule as above, applied to the revamped bene-schema edit/add flow —
  // when the remitter or the beneficiary's bank account is AU-based, the beneficiary's
  // country of residence must match their bank account country, so search against the
  // bank account country. newBeneCountryCode covers both bene-schema-enabled
  // (beneficiary_country_code) and non-bene-schema (country_code) forms.
  const newBeneCountryCode = isEditFlow
    ? currentBene?.country_code || currentBene?.beneficiary_country_code
    : beneficiary_country_code || country_code;
  const newBeneBankAccCountryToUse = isEditFlow
    ? currentBene?.bank_account_country || currentBene?.destination_country
    : bank_account_country;
  const isNewBeneAustracCase = [countryCode, newBeneBankAccCountryToUse].includes('AU');
  const newBeneMismatchedCountry =
    isNewBeneAustracCase && newBeneCountryCode !== newBeneBankAccCountryToUse;

  let addressCountryCode = countryCode;
  if (beneAddressFlow) {
    addressCountryCode = isAustracCase ? selectedBene?.bank_account_country : beneCountryCode;
  } else if (isNewBeneAddressFlow) {
    addressCountryCode = newBeneMismatchedCountry ? newBeneBankAccCountryToUse : newBeneCountryCode;
  }
  const handleOnSuggestionSelect = async (placeId: string) => {
    const response = await handleFetchPlaceDetails(addressCountryCode, placeId);
    if (response) {
      const modifiedResponse = {
        ...response,
        unit_number: response.unitNumber,
        building_name: response.buildingName,
        building_number: response.buildingNumber,
        street_name: response.streetName || response.route,
        ...(isNewBeneAddressFlow ? { street: response.streetName || response.route } : {}),
        street_number: response.streetNumber
      };
      setState((prevState) => ({
        ...prevState,
        retrievedAddress: modifiedResponse,
        keepOpen: false
      }));
      // This spread is required because streetName is coming as route from backend
      retrievedValuesCallBack({ ...modifiedResponse });
    }
  };

  const handleOnSearch = async (value: string) => {
    const eventProperty = {
      type: EventButtonType.SEARCH,
      item: value
    };
    logEventV2(EventNamesV2.ITEM_CLICKED, eventProperty);
    setState((prevState) => ({
      ...prevState,
      loading: true,
      keepOpen: true
    }));
    const response = await handleFetchPlaceId(addressCountryCode, value);
    if (response?.length <= 0) {
      setState((prevState) => ({
        ...prevState,
        showMessage: true,
        suggestions: [],
        loading: false
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        suggestions: response,
        loading: false
      }));
    }
  };

  return (
    <NewAddressSearchContainer>
      <Grid.Column width={16}>
        <Search
          //@ts-ignore
          iconPlacement={iconPlacement}
          toggleShowFields={(value: boolean) => {
            if (value) {
              onEnterManually?.();
              return;
            }
            setState((prevState) => ({ ...prevState, showMessage: value }));
          }}
          newSearchUi={newSearchUi}
          placeholder={searchPlaceHolder}
          countryCode={addressCountryCode}
          keepOpen={state.keepOpen}
          renderSuggestion={({ key, title }: SuggestionType) => (
            <div className="suggestion" key={key}>
              <img src={LOCATION_PIN_ICON} alt="location" />
              <p>{title.length > 63 ? `${title.substring(0, 63)}...` : title}</p>
            </div>
          )}
          suggestions={state.suggestions}
          loading={state.loading}
          onSuggestionSelect={(e: SuggestionType) => handleOnSuggestionSelect(e.value)}
          onSearchChange={(value: string) => handleOnSearch(value)}
          beneAddressFlow={beneAddressFlow || isNewBeneAddressFlow || hideManualAddressLink}
          boxInputSearchNewUi={boxInputSearchNewUi}
        />
      </Grid.Column>
      {state.showMessage && <div className="no-results">{ADDRESS_NOT_FOUND_MSG}</div>}
    </NewAddressSearchContainer>
  );
};

function mapStateToProps(reduxState: IStore) {
  const {
    rootReducer: {
      userStatusReducer: { country_code: countryCode }
    }
  } = reduxState;
  return { countryCode };
}

export default connect<any, any, any, IStore>(mapStateToProps)(NewAddressSearch);
